import { useState, useEffect, useRef } from "react";
import { db } from "../services/firebase";
import { useAuth } from "../context/AuthContext";
import {
  collection, addDoc, orderBy, query,
  serverTimestamp, onSnapshot, where, doc, updateDoc, deleteDoc,
} from "firebase/firestore";
import { ChevronDown, ChevronUp, Pencil, Trash2, X } from "lucide-react";
import { validateWeight, canQuickSaveWeight } from "../utils/weightValidation";
import { hasMeaningfulEntryContent, NO_CONTENT_MESSAGE, normalizeWeightForWrite } from "../utils/entryContent";
import { validateGlucose } from "../utils/glucoseValidation";
import { trackEvent } from "../services/analytics";
import { sortEntriesByDateDesc, formatEntryDateLabel, isFutureEntry, localDateString } from "../utils/entryDate";
import { validateEntryDate, entryDateBounds } from "../utils/dateValidation";

const MELLEKHATÁSOK = [
  { id: "hanyinger", label: "Hányinger", emoji: "🤢" },
  { id: "szekrekedes", label: "Székrekedés", emoji: "😣" },
  { id: "hasmenés", label: "Hasmenés", emoji: "🚽" },
  { id: "fáradtság", label: "Fáradtság", emoji: "😴" },
  { id: "reflux", label: "Reflux", emoji: "🔥" },
  { id: "fejfájás", label: "Fejfájás", emoji: "🤕" },
];

export default function Naplo() {
  const { user } = useAuth();
  const [weight, setWeight] = useState<number | "">("");
  const [dose, setDose] = useState<number | "">("");
  const [customDose, setCustomDose] = useState<string>("");
  const [isCustomDose, setIsCustomDose] = useState(false);
  const [date, setDate] = useState(() => localDateString(new Date()));
  const [mood, setMood] = useState(3);
  const [appetite, setAppetite] = useState(3);
  const [mellekhat, setMellekhat] = useState<Record<string, number>>({});
  const [showMellekhat, setShowMellekhat] = useState(false);
  const [glucose, setGlucose] = useState<number | "">("");
  const [status, setStatus] = useState<null | string>(null);
  const [entries, setEntries] = useState<any[]>([]);
  const [glucoseEnabled, setGlucoseEnabled] = useState(false);
  const [editEntry, setEditEntry] = useState<any | null>(null);
  const [editWeight, setEditWeight] = useState<number | "">("");
  const [editDose, setEditDose] = useState<number | "">("");
  const [editCustomDose, setEditCustomDose] = useState<string>("");
  const [editIsCustomDose, setEditIsCustomDose] = useState(false);
  const [editDate, setEditDate] = useState("");
  const [editMood, setEditMood] = useState(3);
  const [editAppetite, setEditAppetite] = useState(3);
  const [editMellekhat, setEditMellekhat] = useState<Record<string, number>>({});
  const [editShowMellekhat, setEditShowMellekhat] = useState(false);
  const [editGlucose, setEditGlucose] = useState<number | "">("");
  const [editStatus, setEditStatus] = useState<null | string>(null);
  const [editDeleteConfirm, setEditDeleteConfirm] = useState(false);
  const [editDeleting, setEditDeleting] = useState(false);

  const weightError = weight === "" ? null : validateWeight(weight);
  const editWeightError = editWeight === "" ? null : validateWeight(editWeight);
  const glucoseError = validateGlucose(glucose);
  // A dátum-mezők min/max attribútumai. Ugyanabból a függvényből jönnek,
  // mint a mentéskori validateEntryDate() határai — a natív választó így
  // nem enged olyan napot, amit a mentés utána elutasítana.
  const dateBounds = entryDateBounds();
  const editGlucoseError = validateGlucose(editGlucose);

  useEffect(() => {
    if (!user) return;
    const q = query(
      collection(db, "entries"),
      where("userId", "==", user.uid),
      orderBy("createdAt", "desc")
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      // A Firestore-lekérdezés createdAt (beviteli idő) szerint rendez —
      // ez visszamenőleges rögzítésnél (pl. egy év adatának egyszerre
      // felvitele) rossz sorrendet ad. A megjelenítés MINDIG a date
      // (mikor történt) szerint rendezve legyen, ne a beviteli sorrendben.
      const docs: any[] = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setEntries(sortEntriesByDateDesc(docs));
    });
    return () => unsubscribe();
  }, [user]);

  // A meglévő aiEnabled onSnapshot-mintát követi (App.tsx) — a kapcsoló
  // azonnal érvényesül, oldalújratöltés nélkül.
  useEffect(() => {
    if (!user) return;
    const unsubscribe = onSnapshot(doc(db, "profiles", user.uid), (snap) => {
      setGlucoseEnabled(snap.exists() ? (snap.data()?.glucoseEnabled ?? false) : false);
    });
    return () => unsubscribe();
  }, [user]);

  const effectiveDose = isCustomDose ? (parseFloat(customDose) || 0) : dose;

  // B4 (külső audit, 2026-10-14): lassú mobilhálózaton a második koppintás
  // második bejegyzést írt. A `ref` SZINKRON őr — a state-frissítés csak a
  // következő renderben érvényesülne, a gyors dupla koppintás addigra már
  // bent lenne. A `saving` state a gombok letiltásához kell. A két mentési
  // út (teljes űrlap, gyors súly) ugyanazt az őrt használja.
  const savingRef = useRef(false);
  const [saving, setSaving] = useState(false);
  const beginSave = () => {
    if (savingRef.current) return false;
    savingRef.current = true;
    setSaving(true);
    return true;
  };
  const endSave = () => {
    savingRef.current = false;
    setSaving(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // D2 (2026-09-15): a súly NEM kötelező önmagában. Ha meg van adva,
    // továbbra is érvényesnek kell lennie — a jelenlét-követelmény oldódott
    // fel, az érvényességi nem.
    if (weight !== "") {
      const weightValidationError = validateWeight(weight);
      if (weightValidationError) {
        setStatus(`❌ ${weightValidationError}`);
        return;
      }
    }
    if (!hasMeaningfulEntryContent({ weight, dose: effectiveDose, glucose, glucoseEnabled, mellekhat })) {
      setStatus(`❌ ${NO_CONTENT_MESSAGE}`);
      return;
    }
    if (glucoseEnabled && glucoseError) {
      setStatus(`❌ ${glucoseError}`);
      return;
    }
    const dateError = validateEntryDate(date);
    if (dateError) {
      setStatus(`❌ ${dateError}`);
      return;
    }
    if (!beginSave()) return;
    setStatus("Mentés...");
    try {
      await addDoc(collection(db, "entries"), {
        userId: user!.uid,
        // 2026-09-15 (QA N1): a súly ÜRES STRINGKÉNT nem mehet ki. A
        // firestore.rules validWeight-je a hiányzó vagy null mezőt engedi,
        // az üres stringet NEM (jelen van, nem null, nem szám) — így a súly
        // nélküli mentés a szabályon hasalt el, hiába engedte a kliens.
        // Ugyanaz a normalizálás, mint a glucose-nál két sorral lejjebb.
        weight: normalizeWeightForWrite(weight),
        dose: effectiveDose,
        mood,
        appetite,
        mellekhat,
        date,
        ...(glucoseEnabled ? { glucose: glucose === "" ? null : glucose } : {}),
        createdAt: serverTimestamp(),
      });
      // Csak az esemény ténye, érték nélkül — naplóadat (egészségügyi adat)
      // soha nem kerülhet analytics-be (CLAUDE.md 1. szabály).
      if (glucoseEnabled && glucose !== "") trackEvent("glucose_value_logged");
      setStatus("✅ Elmentve!");
      setWeight(""); setDose(""); setCustomDose(""); setIsCustomDose(false);
      setMood(3); setAppetite(3); setMellekhat({}); setGlucose("");
      setDate(localDateString(new Date()));
      setTimeout(() => setStatus(null), 2500);
    } catch (err) {
      setStatus("❌ Hiba mentés közben.");
    } finally {
      endSave();
    }
  };

  const setMh = (id: string, val: number) => {
    setMellekhat(prev => val === 0 ? (({ [id]: _, ...rest }) => rest)(prev) : { ...prev, [id]: val });
  };

  const handleQuickWeight = async () => {
    const weightValidationError = validateWeight(weight);
    if (weightValidationError) {
      setStatus(`❌ ${weightValidationError}`);
      return;
    }
    const dateError = validateEntryDate(date);
    if (dateError) {
      setStatus(`❌ ${dateError}`);
      return;
    }
    if (!beginSave()) return;
    setStatus("Mentés...");
    try {
      await addDoc(collection(db, "entries"), {
        userId: user!.uid,
        // 2026-09-15 (QA N1): a súly ÜRES STRINGKÉNT nem mehet ki. A
        // firestore.rules validWeight-je a hiányzó vagy null mezőt engedi,
        // az üres stringet NEM (jelen van, nem null, nem szám) — így a súly
        // nélküli mentés a szabályon hasalt el, hiába engedte a kliens.
        // Ugyanaz a normalizálás, mint a glucose-nál két sorral lejjebb.
        weight: normalizeWeightForWrite(weight),
        dose: 0,
        mood: 0,
        appetite: 0,
        mellekhat: {},
        date,
        createdAt: serverTimestamp(),
      });
      setStatus("✅ Elmentve!");
      setWeight("");
      setDate(localDateString(new Date()));
      setTimeout(() => setStatus(null), 2500);
    } catch {
      setStatus("❌ Hiba.");
    } finally {
      endSave();
    }
  };

  const openEdit = (item: any) => {
    setEditEntry(item);
    setEditWeight(item.weight ?? "");
    const knownDoses = [2.5,5,7.5,10,12.5,15,0.25,0.5,1,1.7,2.4];
    if (item.dose && !knownDoses.includes(item.dose)) {
      setEditIsCustomDose(true);
      setEditCustomDose(String(item.dose));
      setEditDose("");
    } else {
      setEditIsCustomDose(false);
      setEditCustomDose("");
      setEditDose(item.dose ?? "");
    }
    setEditDate(item.date ?? localDateString(new Date()));
    setEditMood(item.mood ?? 3);
    setEditAppetite(item.appetite ?? 3);
    setEditMellekhat(item.mellekhat ?? {});
    setEditShowMellekhat(false);
    setEditGlucose(item.glucose ?? "");
    setEditStatus(null);
    setEditDeleteConfirm(false);
    setEditDeleting(false);
  };

  const closeEdit = () => setEditEntry(null);

  const handleEditSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editEntry) return;
    // A tartalom-ellenőrzésnek szüksége van a tényleges dózisra, ezért a
    // feloldás a validáció ELÉ került (korábban a mentés előtt állt).
    const effectiveEditDose = editIsCustomDose ? (parseFloat(editCustomDose) || 0) : editDose;
    // D2 (2026-09-15) — ld. a handleSubmit indoklását.
    if (editWeight !== "") {
      const weightValidationError = validateWeight(editWeight);
      if (weightValidationError) {
        setEditStatus(`❌ ${weightValidationError}`);
        return;
      }
    }
    if (!hasMeaningfulEntryContent({ weight: editWeight, dose: effectiveEditDose, glucose: editGlucose, glucoseEnabled, mellekhat: editMellekhat })) {
      setEditStatus(`❌ ${NO_CONTENT_MESSAGE}`);
      return;
    }
    if (glucoseEnabled && editGlucoseError) {
      setEditStatus(`❌ ${editGlucoseError}`);
      return;
    }
    const editDateError = validateEntryDate(editDate);
    if (editDateError) {
      setEditStatus(`❌ ${editDateError}`);
      return;
    }
    setEditStatus("Mentés...");
    try {
      await updateDoc(doc(db, "entries", editEntry.id), {
        // 2026-09-15 (QA N1): a súly ÜRES STRINGKÉNT nem mehet ki. A
        // firestore.rules validWeight-je a hiányzó vagy null mezőt engedi,
        // az üres stringet NEM (jelen van, nem null, nem szám) — így a súly
        // nélküli mentés a szabályon hasalt el, hiába engedte a kliens.
        // Ugyanaz a normalizálás, mint a glucose-nál két sorral lejjebb.
        weight: normalizeWeightForWrite(editWeight),
        dose: effectiveEditDose,
        mood: editMood,
        appetite: editAppetite,
        mellekhat: editMellekhat,
        date: editDate,
        ...(glucoseEnabled ? { glucose: editGlucose === "" ? null : editGlucose } : {}),
      });
      if (glucoseEnabled && editGlucose !== "") trackEvent("glucose_value_logged");
      setEditStatus("✅ Elmentve!");
      setTimeout(() => { setEditStatus(null); closeEdit(); }, 1500);
    } catch {
      setEditStatus("❌ Hiba mentés közben.");
    }
  };

  const handleEditDelete = async () => {
    if (!editEntry) return;
    setEditDeleting(true);
    try {
      await deleteDoc(doc(db, "entries", editEntry.id));
      closeEdit();
    } catch {
      setEditStatus("❌ Hiba törlés közben.");
      setEditDeleting(false);
    }
  };

  const setEditMh = (id: string, val: number) => {
    setEditMellekhat(prev => val === 0 ? (({ [id]: _, ...rest }) => rest)(prev) : { ...prev, [id]: val });
  };

  return (
    <div className="max-w-md mx-auto px-4 pt-6 pb-6 space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Napló</h1>
        <p className="text-sm text-gray-500 mt-0.5">Új bejegyzés rögzítése</p>
      </div>

      {/* Gyors súlyrögzítés */}
      <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm">
        <p className="text-xs font-semibold text-gray-500 mb-2">⚡ Gyors súlyrögzítés</p>
        <div className="flex gap-2">
          <input
            type="number"
            value={weight}
            onChange={(e) => setWeight(e.target.value === "" ? "" : Number(e.target.value))}
            placeholder="pl. 95.5 kg"
            className="flex-1 min-w-0 border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
          />
          <button
            type="button"
            onClick={handleQuickWeight}
            disabled={saving || !canQuickSaveWeight(weight)}
            className="bg-emerald-700 text-white px-4 rounded-xl text-sm font-semibold hover:bg-emerald-800 transition-colors disabled:opacity-60 whitespace-nowrap"
          >
            Mentés
          </button>
        </div>
        {weightError && <p className="mt-1.5 text-xs text-red-600">{weightError}</p>}
      </div>

      {/* Elválasztó */}
      <div className="flex items-center gap-3">
        <div className="flex-1 h-px bg-gray-100" />
        <span className="text-xs text-gray-400">vagy teljes bejegyzés</span>
        <div className="flex-1 h-px bg-gray-100" />
      </div>

      <form onSubmit={handleSubmit} noValidate className="space-y-4">
        {/* Dátum */}
        <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm">
          <label className="text-xs text-gray-500 block mb-1">Dátum</label>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            min={dateBounds.min}
            max={dateBounds.max}
            className="w-full text-base font-semibold text-gray-900 focus:outline-none bg-transparent"
          />
          {isFutureEntry({ date }) && (
            <p className="text-xs text-gray-500 mt-1">A megadott dátum a jövőben van. Biztos, hogy jó?</p>
          )}
        </div>

        {/* Súly + Dózis */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm">
            <label className="text-xs text-gray-500 block mb-1">Súly (kg)</label>
            <input
              type="number" value={weight} required
              onChange={(e) => setWeight(e.target.value === "" ? "" : Number(e.target.value))}
              placeholder="pl. 95.5"
              className="w-full text-xl font-bold text-gray-900 focus:outline-none bg-transparent"
            />
            {weightError && <p className="mt-1 text-xs text-red-600">{weightError}</p>}
          </div>
          <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm">
            <label className="text-xs text-gray-500 block mb-1">Injekció (mg)</label>
            <select
              value={isCustomDose ? "custom" : dose}
              onChange={(e) => {
                if (e.target.value === "custom") {
                  setIsCustomDose(true);
                  setDose("");
                } else {
                  setIsCustomDose(false);
                  setCustomDose("");
                  setDose(Number(e.target.value));
                }
              }}
              className="w-full text-xl font-bold text-gray-900 focus:outline-none bg-transparent"
            >
              <option value="">–</option>
              {[2.5,5,7.5,10,12.5,15,0.25,0.5,1,1.7,2.4].map(d => (
                <option key={d} value={d}>{d} mg</option>
              ))}
              <option value="custom">✏️ Egyedi...</option>
            </select>
            {isCustomDose && (
              <input
                type="number"
                step="0.01"
                min="0"
                value={customDose}
                onChange={(e) => setCustomDose(e.target.value)}
                placeholder="pl. 3.2"
                autoFocus
                className="mt-2 w-full text-xl font-bold text-gray-900 border-b border-emerald-400 focus:outline-none bg-transparent"
              />
            )}
          </div>
        </div>

        {/* Éhomi vércukor — csak bekapcsolt profil-opció esetén */}
        {glucoseEnabled && (
          <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm">
            <label className="text-xs text-gray-500 block mb-1">Éhomi vércukor (mmol/l)</label>
            <input
              type="number"
              step="0.1"
              value={glucose}
              onChange={(e) => setGlucose(e.target.value === "" ? "" : Number(e.target.value))}
              placeholder="pl. 5.6 (nem kötelező)"
              className="w-full text-xl font-bold text-gray-900 focus:outline-none bg-transparent"
            />
            {glucoseError && <p className="mt-1 text-xs text-red-600">{glucoseError}</p>}
          </div>
        )}

        {/* Közérzet */}
        <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm">
          <label className="text-xs text-gray-500 block mb-3">Közérzet</label>
          <div className="flex justify-between gap-2">
            {[
              { v: 1, e: "😞", l: "Rossz" },
              { v: 2, e: "😕", l: "Gyenge" },
              { v: 3, e: "😐", l: "Közepes" },
              { v: 4, e: "🙂", l: "Jó" },
              { v: 5, e: "😄", l: "Kiváló" },
            ].map(({ v, e, l }) => (
              <button key={v} type="button" onClick={() => setMood(v)}
                className={`flex-1 flex flex-col items-center py-2 rounded-xl text-xs transition-all ${
                  mood === v ? "bg-emerald-100 text-emerald-700 font-semibold" : "text-gray-400 hover:bg-gray-50"
                }`}
              >
                <span className="text-lg">{e}</span>
                <span>{l}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Éhség */}
        <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm">
          <label className="text-xs text-gray-500 block mb-3">Éhség szintje</label>
          <div className="flex justify-between gap-2">
            {[
              { v: 1, e: "🟢", l: "Semmi" },
              { v: 2, e: "🟡", l: "Alacsony" },
              { v: 3, e: "🟠", l: "Közepes" },
              { v: 4, e: "🔴", l: "Magas" },
              { v: 5, e: "🚨", l: "Erős" },
            ].map(({ v, e, l }) => (
              <button key={v} type="button" onClick={() => setAppetite(v)}
                className={`flex-1 flex flex-col items-center py-2 rounded-xl text-xs transition-all ${
                  appetite === v ? "bg-orange-100 text-orange-700 font-semibold" : "text-gray-400 hover:bg-gray-50"
                }`}
              >
                <span className="text-lg">{e}</span>
                <span>{l}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Mellékhatások */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <button
            type="button"
            onClick={() => setShowMellekhat(!showMellekhat)}
            className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-gray-700"
          >
            <span>
              Mellékhatások
              {Object.keys(mellekhat).length > 0 && (
                <span className="ml-2 bg-red-100 text-red-700 text-xs px-2 py-0.5 rounded-full">
                  {Object.keys(mellekhat).length} db
                </span>
              )}
            </span>
            {showMellekhat ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>

          {showMellekhat && (
            <div className="px-4 pb-4 space-y-3 border-t border-gray-50">
              {MELLEKHATÁSOK.map(({ id, label, emoji }) => (
                <div key={id} className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">{emoji} {label}</span>
                  <div className="flex gap-1">
                    {[0,1,2,3,4,5].map(v => (
                      <button
                        key={v} type="button"
                        onClick={() => setMh(id, v)}
                        className={`w-7 h-7 rounded-lg text-xs font-semibold transition-all ${
                          (mellekhat[id] ?? 0) === v
                            ? v === 0 ? "bg-gray-200 text-gray-600"
                              : v <= 2 ? "bg-yellow-200 text-yellow-800"
                              : v <= 4 ? "bg-orange-200 text-orange-800"
                              : "bg-red-200 text-red-800"
                            : "bg-gray-50 text-gray-400 hover:bg-gray-100"
                        }`}
                      >
                        {v === 0 ? "–" : v}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
              <p className="text-xs text-gray-400">0 = nincs, 5 = súlyos</p>
            </div>
          )}
        </div>

        <button type="submit" disabled={saving || !!weightError || (glucoseEnabled && !!glucoseError)}
          className="w-full bg-emerald-700 text-white py-3.5 rounded-2xl font-semibold hover:bg-emerald-800 transition-colors disabled:opacity-60"
        >
          Mentés
        </button>
      </form>

      {status && (
        <p className="text-center text-sm font-medium text-gray-600">{status}</p>
      )}

      {/* Bejegyzések listája */}
      <div>
        <h2 className="text-base font-semibold text-gray-700 mb-3">Korábbi bejegyzések</h2>
        {entries.length === 0 && (
          <p className="text-gray-400 text-sm text-center py-4">Még nincs bejegyzés.</p>
        )}
        <div className="space-y-2">
          {entries.map((item) => {
            const itemDate = formatEntryDateLabel(item) || "–";
            const mhCount = item.mellekhat ? Object.keys(item.mellekhat).filter(k => item.mellekhat[k] > 0).length : 0;
            return (
              <div key={item.id} className="bg-white border border-gray-100 rounded-2xl p-3 flex items-center justify-between shadow-sm">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center">
                    <span className="text-emerald-700 font-bold text-sm">{item.weight}</span>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-800">{item.weight} kg</p>
                    <p className="text-xs text-gray-400 flex items-center gap-1.5">
                      {itemDate}
                      {isFutureEntry(item) && (
                        <span className="text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">jövőbeli dátum</span>
                      )}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    {item.dose > 0 && <p className="text-xs font-semibold text-blue-600">{item.dose} mg</p>}
                    {glucoseEnabled && item.glucose != null && (
                      <p className="text-xs font-semibold text-gray-600">{item.glucose} mmol/l</p>
                    )}
                    {mhCount > 0 && <p className="text-xs text-red-400">{mhCount} mellékhatás</p>}
                  </div>
                  <button
                    type="button"
                    onClick={() => openEdit(item)}
                    className="p-1.5 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                  >
                    <Pencil size={14} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Szerkesztő modal */}
      {editEntry && (
        // z-[60], NEM z-50: az App.tsx alsó navigációja is z-50, és a DOM-ban
        // utána jön — azonos z-index esetén a nav festődik a modál fölé,
        // eltakarva/blokkolva a lábrész gombjait (ez volt a görgetési hiba
        // valódi oka, nem csak a magasság-számítás).
        <div className="fixed inset-0 z-[60] flex items-end justify-center bg-black/40" onClick={closeEdit}>
          <div
            className="modal-sheet bg-gray-50 w-full max-w-md rounded-t-3xl flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 pt-5 pb-1 flex-shrink-0">
              <h2 className="text-base font-bold text-gray-900">Bejegyzés szerkesztése</h2>
              <button type="button" onClick={closeEdit} className="p-1.5 text-gray-400 hover:text-gray-700 rounded-lg">
                <X size={18} />
              </button>
            </div>

            <form
              id="edit-entry-form"
              onSubmit={handleEditSave}
              noValidate
              className="modal-scroll flex-1 overflow-y-auto px-5 py-4 space-y-4"
            >
              {/* Dátum */}
              <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm">
                <label className="text-xs text-gray-500 block mb-1">Dátum</label>
                <input
                  type="date"
                  value={editDate}
                  onChange={(e) => setEditDate(e.target.value)}
                  min={dateBounds.min}
                  max={dateBounds.max}
                  className="w-full text-base font-semibold text-gray-900 focus:outline-none bg-transparent"
                />
                {isFutureEntry({ date: editDate }) && (
                  <p className="text-xs text-gray-500 mt-1">A megadott dátum a jövőben van. Biztos, hogy jó?</p>
                )}
              </div>

              {/* Súly + Dózis */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm">
                  <label className="text-xs text-gray-500 block mb-1">Súly (kg)</label>
                  <input
                    type="number" value={editWeight} required
                    onChange={(e) => setEditWeight(e.target.value === "" ? "" : Number(e.target.value))}
                    placeholder="pl. 95.5"
                    className="w-full text-xl font-bold text-gray-900 focus:outline-none bg-transparent"
                  />
                  {editWeightError && <p className="mt-1 text-xs text-red-600">{editWeightError}</p>}
                </div>
                <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm">
                  <label className="text-xs text-gray-500 block mb-1">Injekció (mg)</label>
                  <select
                    value={editIsCustomDose ? "custom" : editDose}
                    onChange={(e) => {
                      if (e.target.value === "custom") {
                        setEditIsCustomDose(true);
                        setEditDose("");
                      } else {
                        setEditIsCustomDose(false);
                        setEditCustomDose("");
                        setEditDose(Number(e.target.value));
                      }
                    }}
                    className="w-full text-xl font-bold text-gray-900 focus:outline-none bg-transparent"
                  >
                    <option value="">–</option>
                    {[2.5,5,7.5,10,12.5,15,0.25,0.5,1,1.7,2.4].map(d => (
                      <option key={d} value={d}>{d} mg</option>
                    ))}
                    <option value="custom">✏️ Egyedi...</option>
                  </select>
                  {editIsCustomDose && (
                    <input
                      type="number" step="0.01" min="0"
                      value={editCustomDose}
                      onChange={(e) => setEditCustomDose(e.target.value)}
                      placeholder="pl. 3.2"
                      autoFocus
                      className="mt-2 w-full text-xl font-bold text-gray-900 border-b border-emerald-400 focus:outline-none bg-transparent"
                    />
                  )}
                </div>
              </div>

              {/* Éhomi vércukor — csak bekapcsolt profil-opció esetén */}
              {glucoseEnabled && (
                <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm">
                  <label className="text-xs text-gray-500 block mb-1">Éhomi vércukor (mmol/l)</label>
                  <input
                    type="number"
                    step="0.1"
                    value={editGlucose}
                    onChange={(e) => setEditGlucose(e.target.value === "" ? "" : Number(e.target.value))}
                    placeholder="pl. 5.6 (nem kötelező)"
                    className="w-full text-xl font-bold text-gray-900 focus:outline-none bg-transparent"
                  />
                  {editGlucoseError && <p className="mt-1 text-xs text-red-600">{editGlucoseError}</p>}
                </div>
              )}

              {/* Közérzet */}
              <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm">
                <label className="text-xs text-gray-500 block mb-3">Közérzet</label>
                <div className="flex justify-between gap-2">
                  {[
                    { v: 1, e: "😞", l: "Rossz" },
                    { v: 2, e: "😕", l: "Gyenge" },
                    { v: 3, e: "😐", l: "Közepes" },
                    { v: 4, e: "🙂", l: "Jó" },
                    { v: 5, e: "😄", l: "Kiváló" },
                  ].map(({ v, e, l }) => (
                    <button key={v} type="button" onClick={() => setEditMood(v)}
                      className={`flex-1 flex flex-col items-center py-2 rounded-xl text-xs transition-all ${
                        editMood === v ? "bg-emerald-100 text-emerald-700 font-semibold" : "text-gray-400 hover:bg-gray-50"
                      }`}
                    >
                      <span className="text-lg">{e}</span>
                      <span>{l}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Éhség */}
              <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm">
                <label className="text-xs text-gray-500 block mb-3">Éhség szintje</label>
                <div className="flex justify-between gap-2">
                  {[
                    { v: 1, e: "🟢", l: "Semmi" },
                    { v: 2, e: "🟡", l: "Alacsony" },
                    { v: 3, e: "🟠", l: "Közepes" },
                    { v: 4, e: "🔴", l: "Magas" },
                    { v: 5, e: "🚨", l: "Erős" },
                  ].map(({ v, e, l }) => (
                    <button key={v} type="button" onClick={() => setEditAppetite(v)}
                      className={`flex-1 flex flex-col items-center py-2 rounded-xl text-xs transition-all ${
                        editAppetite === v ? "bg-orange-100 text-orange-700 font-semibold" : "text-gray-400 hover:bg-gray-50"
                      }`}
                    >
                      <span className="text-lg">{e}</span>
                      <span>{l}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Mellékhatások */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                <button
                  type="button"
                  onClick={() => setEditShowMellekhat(!editShowMellekhat)}
                  className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-gray-700"
                >
                  <span>
                    Mellékhatások
                    {Object.keys(editMellekhat).length > 0 && (
                      <span className="ml-2 bg-red-100 text-red-700 text-xs px-2 py-0.5 rounded-full">
                        {Object.keys(editMellekhat).length} db
                      </span>
                    )}
                  </span>
                  {editShowMellekhat ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </button>
                {editShowMellekhat && (
                  <div className="px-4 pb-4 space-y-3 border-t border-gray-50">
                    {MELLEKHATÁSOK.map(({ id, label, emoji }) => (
                      <div key={id} className="flex items-center justify-between">
                        <span className="text-sm text-gray-700">{emoji} {label}</span>
                        <div className="flex gap-1">
                          {[0,1,2,3,4,5].map(v => (
                            <button
                              key={v} type="button"
                              onClick={() => setEditMh(id, v)}
                              className={`w-7 h-7 rounded-lg text-xs font-semibold transition-all ${
                                (editMellekhat[id] ?? 0) === v
                                  ? v === 0 ? "bg-gray-200 text-gray-600"
                                    : v <= 2 ? "bg-yellow-200 text-yellow-800"
                                    : v <= 4 ? "bg-orange-200 text-orange-800"
                                    : "bg-red-200 text-red-800"
                                  : "bg-gray-50 text-gray-400 hover:bg-gray-100"
                              }`}
                            >
                              {v === 0 ? "–" : v}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                    <p className="text-xs text-gray-400">0 = nincs, 5 = súlyos</p>
                  </div>
                )}
              </div>

            </form>

            {/* Akciógombok — sticky lábrész, mindig elérhető görgetéstől függetlenül */}
            <div
              className="flex-shrink-0 border-t border-gray-100 bg-gray-50 px-5 pt-3 space-y-2"
              style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 1rem)" }}
            >
              {editStatus && (
                <p className="text-center text-sm font-medium text-gray-600">{editStatus}</p>
              )}
              {editDeleteConfirm ? (
                <div className="bg-red-50 border border-red-200 rounded-2xl p-3 space-y-2">
                  <p className="text-xs font-semibold text-red-700 text-center">
                    Biztosan törlöd ezt a bejegyzést?
                  </p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setEditDeleteConfirm(false)}
                      className="flex-1 border border-gray-200 text-gray-600 py-2.5 rounded-xl text-sm font-semibold hover:bg-gray-50 transition-colors"
                    >
                      Mégsem
                    </button>
                    <button
                      type="button"
                      onClick={handleEditDelete}
                      disabled={editDeleting}
                      className="flex-1 bg-red-600 text-white py-2.5 rounded-xl text-sm font-semibold hover:bg-red-700 transition-colors disabled:opacity-60"
                    >
                      {editDeleting ? "Törlés..." : "Igen, törlöm"}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setEditDeleteConfirm(true)}
                    aria-label="Bejegyzés törlése"
                    className="flex items-center justify-center border border-red-200 text-red-600 px-4 rounded-2xl hover:bg-red-50 transition-colors"
                  >
                    <Trash2 size={18} />
                  </button>
                  <button
                    type="submit"
                    form="edit-entry-form"
                    disabled={!!editWeightError || (glucoseEnabled && !!editGlucoseError)}
                    className="flex-1 bg-emerald-700 text-white py-3.5 rounded-2xl font-semibold hover:bg-emerald-800 transition-colors disabled:opacity-60"
                  >
                    Mentés
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
